"use client";

import React, { useCallback } from "react";
import { useCarFilters } from "@/hooks/useCarFilters";
import { useURLDeleteKeys } from "@/hooks/useURLState";
import type { FilterTag } from "@/utils/seoHeaderUtils";

interface ActiveFilterTagsProps {
    tags: FilterTag[];
    label?: string;
    className?: string;
}

const SEARCH_QUERY_KEYS = ["q", "page"];

/**
 * Client component for interactive filter tag removal.
 * Kept deliberately small — the server parent renders the static label text,
 * this component only adds the removal affordance.
 */
export default function ActiveFilterTags({
    tags,
    label = "Active filters:",
    className = ""
}: ActiveFilterTagsProps) {
    const {
        selectedMakes,
        setSelectedMakes,
        selectedModels,
        setSelectedModels,
        selectedBodyStyles,
        setSelectedBodyStyles
    } = useCarFilters();
    const [clearSearchQuery] = useURLDeleteKeys(SEARCH_QUERY_KEYS);

    const handleRemove = useCallback(
        (tag: FilterTag) => {
            switch (tag.type) {
                case "search":
                    clearSearchQuery();
                    break;
                case "make":
                    setSelectedMakes(selectedMakes.filter((m) => m !== tag.value));
                    break;
                case "model":
                    setSelectedModels(selectedModels.filter((m) => m !== tag.value));
                    break;
                case "bodyStyle":
                    setSelectedBodyStyles(selectedBodyStyles.filter((b) => b !== tag.value));
                    break;
                // year and price tags are display-only (no removal handler)
            }
        },
        [
            clearSearchQuery,
            selectedMakes,
            setSelectedMakes,
            selectedModels,
            setSelectedModels,
            selectedBodyStyles,
            setSelectedBodyStyles
        ]
    );

    if (tags.length === 0) return null;

    const isRemovable = (type: string) => type !== "year" && type !== "price";

    return (
        <div className={`t-flex t-flex-wrap t-items-center t-gap-1 sm:t-gap-2 t-mb-1 sm:t-mb-2 ${className}`}>
            {label && (
                <span className="t-text-xs sm:t-text-sm t-text-brand-gray-medium t-font-semibold t-font-modern">
                    {label}
                </span>
            )}
            {tags.map((tag) => (
                <span
                    key={`${tag.type}:${tag.value}`}
                    className="t-inline-flex t-items-center t-gap-1 t-px-2 sm:t-px-3 t-py-1 t-bg-brand-yellow t-text-brand-purple t-text-xs sm:t-text-sm t-font-semibold t-rounded-none t-border t-border-brand-yellow-dark t-shadow-sm hover:t-bg-brand-yellow-light t-transition-all t-duration-200"
                >
                    {tag.label}
                    {isRemovable(tag.type) && (
                        <button
                            onClick={() => handleRemove(tag)}
                            className="t-ml-1 t-text-brand-purple hover:t-text-brand-purple-light t-transition-colors t-p-1 t-rounded-none hover:t-bg-white/20"
                            aria-label={`Remove ${tag.label} filter`}
                        >
                            <svg className="t-w-2 sm:t-w-3 t-h-2 sm:t-h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </span>
            ))}
        </div>
    );
}
